/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, KeyboardEvent } from 'react';
import { onClickGoogleAnalyticsEvent } from '../utils/ga-utils';
import { BiButton, BiTypography } from 'bi-web-components-react';
import { BiIconPhone, BiIconChat, BiIconMail } from '@bi-digital/icons-react';
import { useTranslation } from 'react-i18next';
import type { ComponentSelector } from '../types';

export type FrontContentProps = {
    setComponent: (val: ComponentSelector) => void;
    click: () => void;
    innerChatButton: string;
};

declare global {
    interface Window {
        startpageProperties: any;
    }
}

window.startpageProperties = window.startpageProperties || {};

export function FrontContent({ setComponent, click, innerChatButton }: FrontContentProps) {
    const { t } = useTranslation('chat');

    const [zoomLevel, setZoomLevel] = useState(window.devicePixelRatio * 100);

    useEffect(() => {
        const handleResize = () => setZoomLevel(window.devicePixelRatio * 100);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const clicker = (val: ComponentSelector, category: string, action: string, label: string) => {
        setComponent(val);
        onClickGoogleAnalyticsEvent(category, action, label);
    };

    const disableChat = innerChatButton === 'active' ? false : true;
    const chatText = innerChatButton === 'loading' ? t('loadingChat') : disableChat ? t('chatClosed') : t('chatWithUs');

    return (
        <div className='FrontContentContainer'>
            <div className='maintext'>
                <div className='opening-hours'>
                    <BiTypography fontStyle='bold'>
                        <p>{t('mainText')}</p>
                    </BiTypography>

                    <BiTypography>
                        {window.startpageProperties?.mainTextHours1 && (
                            <p>
                                <span>{t('mainTextDays1')}</span>{' '}
                                <span>{window.startpageProperties?.mainTextHours1}</span>
                            </p>
                        )}
                        {window.startpageProperties?.mainTextChatHours && (
                            <p>{window.startpageProperties?.mainTextChatHours}</p>
                        )}
                    </BiTypography>

                    {window.startpageProperties?.mainTextHours2 && (
                        <BiTypography>
                            <p>
                                <span>{t('mainTextDays2')}</span>{' '}
                                <span>{window.startpageProperties?.mainTextHours2}</span>
                            </p>
                        </BiTypography>
                    )}
                </div>
            </div>
            <div className=''>
                <div className='sfMainButtons'>
                    <BiButton
                        stretch
                        tabIndex={0}
                        id='innerChatButton'
                        disabled={disableChat}
                        onClick={click}
                        size={zoomLevel === 400 ? 'small' : 'medium'}
                        aria-label={chatText}
                    >
                        <BiIconChat slot='icon-left' aria-hidden='true' />
                        {chatText}
                    </BiButton>
                    <BiButton
                        stretch
                        id='contactFormButton'
                        tabIndex={0}
                        onClick={() => clicker('contactForm', 'Chat', 'Contact form', window.location.href)}
                        size={zoomLevel === 400 ? 'small' : 'medium'}
                        aria-label={t('contactForm')}
                    >
                        <BiIconMail slot='icon-left' aria-hidden='true' />
                        {t('contactForm')}
                    </BiButton>
                    <a
                        href='tel:+4746410000'
                        className='callUs'
                        target='_parent'
                        tabIndex={-1}
                        aria-label={t('callUs')}
                    >
                        <BiButton
                            stretch
                            aria-label={t('callUs')}
                            size={zoomLevel === 400 ? 'small' : 'medium'}
                            tabIndex={0}
                            onClick={() => onClickGoogleAnalyticsEvent('Chat', 'Call us', window.location.href)}
                            onKeyDown={(e: KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === 'Space') {
                                    onClickGoogleAnalyticsEvent('Chat', 'Call us', window.location.href);
                                }
                            }}
                        >
                            <BiIconPhone slot='icon-left' aria-hidden='true' />
                            {`${t('callUs')} 464 10 000`}
                        </BiButton>
                    </a>
                </div>
            </div>
        </div>
    );
}
